/**
 * CPV-AI-01 · the CPV assistant's door to the server (`/bp/cpv-ai/…`, views/Cpv/ai_view.py).
 *
 *   ask(body)                     POST cpv-ai/query/ {question, intent?, product?, parameter?, signal?, period_from?,
 *                                 period_to?, batches?} → the stored answer
 *   summariseSignal(id, body)     POST cpv-ai/signals/<id>/summarise/ {product?, parameter?, period_from?, period_to?,
 *                                 batches?} → the stored signal summary
 *   suggestions(context)          GET  cpv-ai/suggestions/ → {suggestions: [{intent, prompt, available, note}], context, label}
 *   feedback(id, action, text)    POST cpv-ai/feedback/ {response, action: HELPFUL | NOT_HELPFUL, comments} → the answer
 *   responses(params)             GET  cpv-ai/responses/ — the caller's own answers, newest first (one page)
 *   response(id)                  GET  cpv-ai/responses/<id>/
 *   draftReport(id, sections)     POST cpv-ai/reports/draft/ {report, sections?} → the stored narrative draft of a DRAFT
 *                                 periodic report (AI-4)
 *   draftApqr(body)               POST cpv-ai/apqr/section-ii/draft/ {product, period_from, period_to, sections?}
 *   investigateSignal(id)         POST cpv-ai/signals/<id>/investigate/ {} → the stored investigation outline (AI-6)
 *   investigateInsight(id)        POST cpv-ai/insights/<id>/investigate/ {} → the same outline of an insight
 *
 * Every POST carries its Idempotency-Key (cpvMasterApi.post); the plants are the header choice the interceptor adds.
 * The client decides nothing: the answer, its evidence, what is available and every refusal are the server's.
 */
import { get, post } from '../cpvMasterApi';

const AI = '/cpv-ai';

/** The context fields the server reads, without the empty ones. */
export const CONTEXT_FIELDS = ['product', 'parameter', 'signal', 'period_from', 'period_to', 'batches'];
export function contextBody(context) {
  return Object.fromEntries(CONTEXT_FIELDS.map((k) => [k, context?.[k]]).filter(([, v]) => v !== undefined && v !== null && v !== ''));
}

export const assistantApi = {
  ask: ({ question, intent, ...context }) => post(`${AI}/query/`, {
    ...(question ? { question } : {}), ...(intent ? { intent } : {}), ...contextBody(context),
  }),
  summariseSignal: (id, context) => post(`${AI}/signals/${id}/summarise/`, contextBody({ ...context, signal: undefined })),
  suggestions: (context) => get(`${AI}/suggestions/`, contextBody(context)),
  feedback: (response, action, comments = '') => post(`${AI}/feedback/`, { response, action, comments }),
  responses: (params) => get(`${AI}/responses/`, params),
  response: (id) => get(`${AI}/responses/${id}/`),
  draftReport: (report, sections) => post(`${AI}/reports/draft/`, { report, ...(sections?.length ? { sections } : {}) }),
  investigateSignal: (id) => post(`${AI}/signals/${id}/investigate/`, {}),
  investigateInsight: (id) => post(`${AI}/insights/${id}/investigate/`, {}),
  draftApqr: ({ product, period_from: from, period_to: to, sections }) => post(`${AI}/apqr/section-ii/draft/`, {
    product, ...(from ? { period_from: from } : {}), ...(to ? { period_to: to } : {}), ...(sections?.length ? { sections } : {}),
  }),
};

/** The closed words of the answer (engines/vocabulary/cpv_ai.py). */
export const INTENT_UNSUPPORTED = 'UNSUPPORTED';
export const FINDING = { FACT: 'observed_fact', HYPOTHESIS: 'hypothesis' };
export const EVIDENCE_UNAVAILABLE = 'UNAVAILABLE';
export const ACTION = { HELPFUL: 'HELPFUL', NOT_HELPFUL: 'NOT_HELPFUL' };

/** What the answer is, for the thread's state: unsupported, nothing found to cite, or an answer. */
export function answerState(r) {
  if (!r) return null;
  if (r.intent === INTENT_UNSUPPORTED) return 'unsupported';
  const a = r.answer || {};
  if (!(a.findings || []).length && !(r.evidence || []).length) return 'insufficient';
  return 'answer';
}

/** The answer as plain text, for "Copy answer". */
export function answerText(r) {
  const a = r?.answer || {};
  const lines = [r?.label, '', a.summary];
  const list = (title, rows) => { if (rows.length) lines.push('', title, ...rows.map((x) => `- ${x}`)); };
  list('Findings', (a.findings || []).map((f) => `${f.text}${(f.evidence || []).length ? ` [${f.evidence.join(', ')}]` : ''}`));
  list('Hypotheses (not verified)', (a.hypotheses || []).map((h) => h.text));
  list('Limitations', a.limitations || []);
  list('Questions to review', a.review_questions || []);
  list('Sources', (r?.evidence || []).map((e) => `${e.key} ${e.display_label}`));
  return lines.filter((l) => l !== undefined && l !== null).join('\n');
}

/** A report draft's narrative as plain text, for "Insert into narrative": each section's title, then its paragraphs. The
 *  evidence keys stay with the draft (they mean nothing inside the report). */
export function draftNarrative(r) {
  const sections = r?.answer?.sections || [];
  return sections.map((s) => [s.title, ...(s.paragraphs || []).map((p) => (p.sentences || []).map((x) => x.text).join(' '))]
    .join('\n\n')).join('\n\n');
}

/** A report draft as plain text, for "Copy": the label, the summary, the sections with their evidence keys, the data
 *  coverage, the limitations and the sources. */
export function draftText(r) {
  const a = r?.answer || {};
  const lines = [r?.label, '', a.summary];
  (a.sections || []).forEach((s) => {
    lines.push('', s.title);
    (s.paragraphs || []).forEach((p) => lines.push((p.sentences || [])
      .map((x) => `${x.text}${(x.evidence || []).length ? ` [${x.evidence.join(', ')}]` : ''}`).join(' ')));
  });
  const list = (title, rows) => { if (rows.length) lines.push('', title, ...rows.map((x) => `- ${x}`)); };
  list('Data coverage', a.coverage || []);
  list('Limitations', a.limitations || []);
  list('Sources', (r?.evidence || []).map((e) => `${e.key} ${e.display_label}`));
  return lines.filter((l) => l !== undefined && l !== null).join('\n');
}

/** An investigation outline (AI-6) as plain text, for "Copy to assessment notes": the label, the summary, then under the
 *  server's headings what we know, each hypothesis (not verified) with its evidence needed, and the questions. The
 *  evidence keys stay with the answer (they mean nothing inside an assessment). */
export function investigationNotes(r) {
  const a = r?.answer || {};
  const h = a.investigation?.headings || {};
  const lines = [r?.label, '', a.summary];
  const list = (title, rows) => { if (rows.length) lines.push('', title, ...rows); };
  list(h.known, (a.findings || []).map((f) => `- ${f.text}`));
  list(h.hypotheses, (a.hypotheses || []).flatMap((x, i) => [
    `${i + 1}. ${x.text} (${x.status})`, ...(x.evidence_needed || []).map((n) => `   - ${h.evidence_needed}: ${n.text}`)]));
  list(h.questions, (a.review_questions || []).map((q) => `- ${q}`));
  return lines.filter((l) => l !== undefined && l !== null).join('\n');
}
